import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const deliveryLocationCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'DeliveryLocation',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.deliveryLocations.`,
});

export async function getDeliveryLocations(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocations');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONS)');
                await deliveryLocationCache.put('deliveryLocations', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocations', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocations');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocations', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery locations');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery locations available');
            return [];
        }

        return await fetchDeliveryLocationsFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery locations: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocations');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery locations');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getDeliveryLocationTypes(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocationTypes');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DLT');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DLT)');
                await deliveryLocationCache.put('deliveryLocationTypes', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationTypes', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocationTypes');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocationTypes', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery location types');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationTypesInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery location types available');
            return [];
        }

        return await fetchDeliveryLocationTypesFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery location types: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocationTypes');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery location types');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getDeliveryLocationDelays(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocationDelays');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONDELAYS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONDELAYS)');
                await deliveryLocationCache.put('deliveryLocationDelays', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationDelays', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocationDelays');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocationDelays', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery location delays');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationDelaysInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery location delays available');
            return [];
        }

        return await fetchDeliveryLocationDelaysFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery location delays: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocationDelays');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery location delays');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getDeliveryLocationDocuments(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocationDocuments');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONDOCUMENTS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONDOCUMENTS)');
                await deliveryLocationCache.put('deliveryLocationDocuments', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationDocuments', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocationDocuments');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocationDocuments', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery location documents');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationDocumentsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery location documents available');
            return [];
        }

        return await fetchDeliveryLocationDocumentsFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery location documents: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocationDocuments');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery location documents');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getDeliveryLocationsGeo(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocationsGeo');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONGEO');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONGEO)');
                await deliveryLocationCache.put('deliveryLocationsGeo', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationsGeo', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocationsGeo');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocationsGeo', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery locations geo');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationsGeoInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery locations geo available');
            return [];
        }

        return await fetchDeliveryLocationsGeoFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery locations geo: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocationsGeo');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery locations geo');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getDeliveryLocationFurnishings(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocationFurnishings');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('SFA_FURNISHING');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (SFA_FURNISHING)');
                await deliveryLocationCache.put('deliveryLocationFurnishings', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationFurnishings', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocationFurnishings');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocationFurnishings', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery location furnishings');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationFurnishingsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery location furnishings available');
            return [];
        }

        return await fetchDeliveryLocationFurnishingsFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery location furnishings: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocationFurnishings');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery location furnishings');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getDeliveryLocationContacts(deliveryLocationId) {
    try {
        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: Cannot fetch delivery location contacts');
            return [];
        }

        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationContacts'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const contacts = await response.json();

        log('success', 'DeliveryLocation', `✅ Fetched ${contacts.length} delivery location contacts`);
        return contacts;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location contacts from API: ${error.message}`);
        return [];
    }
}

export async function getDeliveryLocationRestrictions(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocationRestrictions');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONRESTRICTIONS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONRESTRICTIONS)');
                await deliveryLocationCache.put('deliveryLocationRestrictions', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationRestrictions', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocationRestrictions');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocationRestrictions', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery location restrictions');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationRestrictionsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery location restrictions available');
            return [];
        }

        return await fetchDeliveryLocationRestrictionsFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery location restrictions: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocationRestrictions');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery location restrictions');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getDeliveryLocationSegmentValidations(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = deliveryLocationCache.getFromMemory('deliveryLocationSegmentValidations');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONSEGMENTVALIDATIONS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONSEGMENTVALIDATIONS)');
                await deliveryLocationCache.put('deliveryLocationSegmentValidations', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationSegmentValidations', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await deliveryLocationCache.get('deliveryLocationSegmentValidations');
        if (cached && !forceRefresh) {
            deliveryLocationCache.setInMemory('deliveryLocationSegmentValidations', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'DeliveryLocation', '📴 Offline: Returning cached delivery location segment validations');
                return cached;
            }

            setTimeout(() => refreshDeliveryLocationSegmentValidationsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: No cached delivery location segment validations available');
            return [];
        }

        return await fetchDeliveryLocationSegmentValidationsFromAPI();
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Error getting delivery location segment validations: ${error.message}`);

        try {
            const fallback = await deliveryLocationCache.get('deliveryLocationSegmentValidations');
            if (fallback) {
                log('info', 'DeliveryLocation', '💾 Using fallback cached delivery location segment validations');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'DeliveryLocation', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

async function fetchDeliveryLocationsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocations'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocations = await response.json();
        await deliveryLocationCache.put('deliveryLocations', deliveryLocations);
        deliveryLocationCache.setInMemory('deliveryLocations', deliveryLocations);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocations.length} delivery locations`);
        return deliveryLocations;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery locations from API: ${error.message}`);
        throw error;
    }
}

async function fetchDeliveryLocationTypesFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationTypes'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocationTypes = await response.json();
        await deliveryLocationCache.put('deliveryLocationTypes', deliveryLocationTypes);
        deliveryLocationCache.setInMemory('deliveryLocationTypes', deliveryLocationTypes);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocationTypes.length} delivery location types`);
        return deliveryLocationTypes;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location types from API: ${error.message}`);
        throw error;
    }
}

async function fetchDeliveryLocationDelaysFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationDelays'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocationDelays = await response.json();
        await deliveryLocationCache.put('deliveryLocationDelays', deliveryLocationDelays);
        deliveryLocationCache.setInMemory('deliveryLocationDelays', deliveryLocationDelays);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocationDelays.length} delivery location delays`);
        return deliveryLocationDelays;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location delays from API: ${error.message}`);
        throw error;
    }
}

async function fetchDeliveryLocationDocumentsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationDocuments'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocationDocuments = await response.json();
        await deliveryLocationCache.put('deliveryLocationDocuments', deliveryLocationDocuments);
        deliveryLocationCache.setInMemory('deliveryLocationDocuments', deliveryLocationDocuments);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocationDocuments.length} delivery location documents`);
        return deliveryLocationDocuments;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location documents from API: ${error.message}`);
        throw error;
    }
}

async function fetchDeliveryLocationsGeoFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationsGeo'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocationsGeo = await response.json();
        await deliveryLocationCache.put('deliveryLocationsGeo', deliveryLocationsGeo);
        deliveryLocationCache.setInMemory('deliveryLocationsGeo', deliveryLocationsGeo);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocationsGeo.length} delivery locations geo`);
        return deliveryLocationsGeo;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery locations geo from API: ${error.message}`);
        throw error;
    }
}

async function fetchDeliveryLocationFurnishingsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationFurnishings'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocationFurnishings = await response.json();
        await deliveryLocationCache.put('deliveryLocationFurnishings', deliveryLocationFurnishings);
        deliveryLocationCache.setInMemory('deliveryLocationFurnishings', deliveryLocationFurnishings);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocationFurnishings.length} delivery location furnishings`);
        return deliveryLocationFurnishings;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location furnishings from API: ${error.message}`);
        throw error;
    }
}

async function fetchDeliveryLocationRestrictionsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationRestrictions'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocationRestrictions = await response.json();
        await deliveryLocationCache.put('deliveryLocationRestrictions', deliveryLocationRestrictions);
        deliveryLocationCache.setInMemory('deliveryLocationRestrictions', deliveryLocationRestrictions);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocationRestrictions.length} delivery location restrictions`);
        return deliveryLocationRestrictions;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location restrictions from API: ${error.message}`);
        throw error;
    }
}

async function fetchDeliveryLocationSegmentValidationsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationSegmentValidations'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const deliveryLocationSegmentValidations = await response.json();
        await deliveryLocationCache.put('deliveryLocationSegmentValidations', deliveryLocationSegmentValidations);
        deliveryLocationCache.setInMemory('deliveryLocationSegmentValidations', deliveryLocationSegmentValidations);

        log('success', 'DeliveryLocation', `✅ Fetched and cached ${deliveryLocationSegmentValidations.length} delivery location segment validations`);
        return deliveryLocationSegmentValidations;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location segment validations from API: ${error.message}`);
        throw error;
    }
}

async function refreshDeliveryLocationsInBackground() {
    try {
        await fetchDeliveryLocationsFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery locations completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshDeliveryLocationTypesInBackground() {
    try {
        await fetchDeliveryLocationTypesFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery location types completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshDeliveryLocationDelaysInBackground() {
    try {
        await fetchDeliveryLocationDelaysFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery location delays completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshDeliveryLocationDocumentsInBackground() {
    try {
        await fetchDeliveryLocationDocumentsFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery location documents completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshDeliveryLocationsGeoInBackground() {
    try {
        await fetchDeliveryLocationsGeoFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery locations geo completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshDeliveryLocationFurnishingsInBackground() {
    try {
        await fetchDeliveryLocationFurnishingsFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery location furnishings completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshDeliveryLocationRestrictionsInBackground() {
    try {
        await fetchDeliveryLocationRestrictionsFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery location restrictions completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

async function refreshDeliveryLocationSegmentValidationsInBackground() {
    try {
        await fetchDeliveryLocationSegmentValidationsFromAPI();
        log('success', 'DeliveryLocation', '🔄 Background refresh of delivery location segment validations completed');
    } catch (error) {
        log('warn', 'DeliveryLocation', `⚠️ Background refresh failed: ${error.message}`);
    }
}

export async function refreshDeliveryLocations() {
    deliveryLocationCache.clearMemory();
    await deliveryLocationCache.clear();
}
